/* eslint-disable max-lines-per-function */
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Backsplash, BlueprintInstanceType, type BacksplashHorizontalAlign } from '@rta/rta-blueprint-library';
import { Box, Button, FormLabel, Select, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { ContextPanelContent } from './context-panel-content';
import { InlineNumberInput } from './controls';
import { lang } from '@lang';
import { popContextPanel, useAppDispatch } from '@state';
import { useDesign } from '@hooks';

export type BacksplashPropertiesPanelProps = {
	id: string | null;
};

export const BacksplashPropertiesPanel = ({ id }: BacksplashPropertiesPanelProps): JSX.Element => {
	const [controller] = useDesign<BpController>();
	const dispatch = useAppDispatch();
	const [backsplash, setBacksplash] = useState<Backsplash | null>(null);
	const sectionHeaderColor = useColorModeValue('gray.600', 'gray.300');

	const readonly = controller?.readonly ?? true;

	useEffect(() => {
		if (controller && id) {
			const refMap = controller.kitchen?.getReferenceMap(id);
			if (refMap?.type === BlueprintInstanceType.Backsplash) {
				setBacksplash(refMap.backsplash as Backsplash);
			}
		}
	}, [controller, id]);

	const handleDelete = () => {
		if (!backsplash || !controller || readonly) return;

		const countertop = controller.kitchen?.getReferenceMap(id ?? '')?.countertop;
		if (countertop) {
			countertop.removeBacksplash(backsplash.id);
			dispatch(popContextPanel());
		}
	};

	if (!controller || !backsplash) return <></>;

	return <ContextPanelContent title={lang.contextPanels.title.backsplashProperties}>
		<Stack pt="10px" spacing="0">
			<Text pl="5px" color={sectionHeaderColor} fontSize="medium" fontWeight="medium">
				Dimensions
			</Text>
			<Box marginBottom={3} padding={2} textAlign="left"
				borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
			>
				<Stack spacing="1" width="100%" height="65%">
					<InlineNumberInput
						category="backsplash"
						label="Width"
						value={backsplash.size?.width ?? 0}
						enabled={!readonly}
						onChange={(valueString) => {
							const newWidth = Number(valueString);
							backsplash.widthOverride = newWidth;
							backsplash.size = { ...backsplash.size, width: newWidth };
						}}
					/>
					<InlineNumberInput
						category="backsplash"
						label="Height"
						min={0.5}
						value={backsplash.size?.height ?? 0}
						enabled={!readonly}
						onChange={(valueString) => {
							backsplash.size = { ...backsplash.size, height: Number(valueString) };
						}}
					/>
					<InlineNumberInput
						category="backsplash"
						label="Depth"
						min={0.25}
						value={backsplash.size?.depth ?? 0}
						enabled={!readonly}
						onChange={(valueString) => {
							backsplash.size = { ...backsplash.size, depth: Number(valueString) };
						}}
					/>
				</Stack>
			</Box>

			<Text pl="5px" color={sectionHeaderColor} fontSize="medium" fontWeight="medium">
				Position
			</Text>
			<Box marginBottom={3} padding={2} textAlign="left"
				borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
			>
				<Stack spacing="2" width="100%">
					<Box>
						<FormLabel htmlFor="backsplash-alignment" fontSize="small">Alignment:</FormLabel>
						<Select
							id="backsplash-alignment"
							fontSize="small"
							value={backsplash.horizontalAlign ?? 'left'}
							disabled={readonly}
							onChange={(event) => {
								backsplash.horizontalAlign = event.target.value as BacksplashHorizontalAlign;
							}}
						>
							<option value="left">Left</option>
							<option value="center">Center</option>
							<option value="right">Right</option>
						</Select>
					</Box>
					<InlineNumberInput
						category="backsplash"
						label="Offset"
						value={backsplash.manualOffset ?? 0}
						enabled={!readonly}
						onChange={(valueString) => {
							backsplash.manualOffset = Number(valueString);
						}}
					/>
					<InlineNumberInput
						category="backsplash"
						label="Padding"
						value={0}
						enabled={false}
					/>
				</Stack>
			</Box>

			{!readonly && (
				<Box padding={2}>
					<Button
						leftIcon={<Trash2 size={16} />}
						colorScheme="red"
						variant="outline"
						size="sm"
						width="100%"
						onClick={handleDelete}
					>
						Delete Backsplash
					</Button>
				</Box>
			)}
		</Stack>
	</ContextPanelContent>;
};
